import { isEqual, isEqualWith, cloneDeep } from 'lodash-es';
import Vue from 'vue';
import Order from '@/services/Order';
import * as types from '../mutation-types';

// initial state
const state = () => ({
  items: [],
  basketItemUpdated: null,
  orderSuccess: false,
  orderName: '',
  saveBasket: false,
});

// getters
const getters = {
  basketItems: (state, getters) => state.items.filter(
    ({ id }) => getters.itemsFlattened.find((product) => product.id === id),
  ).map(({ id, options, quantity }) => {
    const item = getters.itemsFlattened.find((product) => product.id === id);

    const displayOptions = item.options.reduce((opts, option) => {
      const optionId = option.id;
      const selected = option.items.find((item) => item.id === options[optionId]);

      if (selected) {
        let { label } = selected;
        if (option.label) {
          label = `${option.label}: ${label}`;
        }

        opts.push({
          menu_item_option_id: option.id,
          menu_option_group_item_id: selected.id,
          label,
        });
      }

      return opts;
    }, []);

    return {
      id: item.id,
      title: item.title,
      displayOptions,
      options,
      quantity,
    };
  }),

  itemOptions: (state, getters) => state.items.map(({ id, options, quantity }) => {
    const item = getters.itemsFlattened.find((product) => product.id === id);

    return {
      id: item.id,
      title: item.title,
      itemOptions: item.options,
      options,
      quantity,
    };
  }),

  hasBasketItems: (state, getters) => !!getters.basketItems.length,
};

// actions
const actions = {
  addBasketItem({ state, commit }, { item, index }) {
    // check for duplicates
    let foundIndex = state.items.findIndex((basketItem, basketIndex) => {
      // ignore if its the actual item being updated
      if (basketIndex === index) {
        return false;
      }

      // check its the same
      return isEqualWith(basketItem, item, (obj1, obj2) => isEqual(obj1.id, obj2.id)
          && isEqual(obj1.options, obj2.options));
    });

    if (foundIndex > -1) {
      // if updating and have found a duplicate,
      // update the duplicate item and remove this one.
      if (index !== undefined) {
        commit(types.BASKET_ITEM_REMOVE, index);

        // since we have removed an item decrement the index
        if (foundIndex > index) {
          foundIndex--;
        }
      }

      const basketItem = cloneDeep(state.items[foundIndex]);
      basketItem.quantity += item.quantity;

      index = foundIndex;
      item = basketItem;
    }

    if (index !== undefined) {
      commit(types.BASKET_ITEM_UPDATE, { item, index });
      setTimeout(() => {
        commit(types.BASKET_ITEM_UPDATED);
      }, 500);
    } else {
      commit(types.BASKET_ITEM_ADD, item);
    }

    commit(types.MENU_DESELECT_ITEM);
  },

  removeBasketItem({ state, commit }, index) {
    commit(types.BASKET_ITEM_REMOVE, index);
    commit(types.MENU_DESELECT_ITEM);
  },

  selectBasketItem({ state, commit, dispatch }, index) {
    const selectedItem = state.items[index];
    const item = {
      id: selectedItem.id,
      options: { ...selectedItem.options },
      quantity: selectedItem.quantity,
      basketIndex: index,
    };

    dispatch('selectMenuItem', item);
  },

  async placeOrder({ getters, commit }, {
    name, email, location, saveBasket, rememberMe,
  }) {
    const items = getters.basketItems.reduce((items, { id, quantity, displayOptions = [] }) => {
      items.push({
        menu_item_id: id,
        options: displayOptions,
        quantity,
      });

      return items;
    }, []);

    const order = {
      name,
      email,
      location,
      items,
    };

    const orderResponse = await Order.store(order);

    if (!saveBasket) {
      commit(types.BASKET_EMPTY);
    }

    const response = orderResponse.data;

    const rememberName = rememberMe ? { name, email, location } : {};
    commit(types.ORDER_NAME, rememberName);
    commit(types.BASKET_SAVE, saveBasket);

    commit(types.ORDER_SUCCESS, response);
  },

  refreshBasket({ state, getters, commit }) {
    const filteredItems = state.items.filter(({ id }) => getters.itemsFlattened.find((product) => product.id === id));

    commit(types.BASKET_SET_ITEMS, filteredItems);
  },
};

// mutations
const mutations = {
  [types.BASKET_ITEM_ADD](state, item) {
    state.items.push(item);
  },

  [types.BASKET_ITEM_UPDATE](state, { item, index }) {
    Vue.set(state.items, index, item);

    state.basketItemUpdated = index;
  },

  [types.BASKET_ITEM_UPDATED](state) {
    state.basketItemUpdated = null;
  },

  [types.BASKET_ITEM_REMOVE](state, index) {
    state.items.splice(index, 1);
  },

  [types.BASKET_EMPTY](state) {
    state.items = [];
  },

  [types.ORDER_SUCCESS](state, value) {
    state.orderSuccess = value;
  },

  [types.ORDER_NAME](state, value) {
    state.orderName = value;
  },

  [types.BASKET_SAVE](state, value) {
    state.saveBasket = value;
  },

  [types.BASKET_SET_ITEMS](state, value) {
    state.items = value;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
